import { fetch as expoFetch } from "expo/fetch";

import { app, auth, firebaseEmulatorMode } from "./firebase";

type RestValue = Record<string, unknown>;

export class PostFirestoreRestError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status?: number
  ) {
    super(message);
    this.name = "PostFirestoreRestError";
  }
}

export function encodeFirestoreRestValue(value: unknown): RestValue {
  if (value === null) return { nullValue: null };
  if (typeof value === "string") return { stringValue: value };
  if (typeof value === "boolean") return { booleanValue: value };
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new PostFirestoreRestError("invalid-argument", "Nombre Firestore invalide.");
    }
    return Number.isInteger(value)
      ? { integerValue: String(value) }
      : { doubleValue: value };
  }
  if (Array.isArray(value)) {
    return { arrayValue: { values: value.map(encodeFirestoreRestValue) } };
  }
  if (typeof value === "object") {
    return { mapValue: { fields: encodeFirestoreRestFields(value as Record<string, unknown>) } };
  }
  throw new PostFirestoreRestError("invalid-argument", "Valeur Firestore invalide.");
}

export function encodeFirestoreRestFields(data: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(data)
      .filter(([key, value]) => key !== "createdAt" && value !== undefined)
      .map(([key, value]) => [key, encodeFirestoreRestValue(value)])
  );
}

function codeForHttpStatus(status: number) {
  if (status === 400) return "invalid-argument";
  if (status === 401) return "unauthenticated";
  if (status === 403) return "permission-denied";
  if (status === 404) return "not-found";
  if (status === 409) return "already-exists";
  if (status === 429) return "resource-exhausted";
  if (status >= 500) return "unavailable";
  return "unknown";
}

function firestoreRestBaseUrl() {
  return firebaseEmulatorMode
    ? "http://127.0.0.1:8080"
    : "https://firestore.googleapis.com";
}

type CommitPostDependencies = {
  getToken: () => Promise<string>;
  fetchImpl: typeof fetch;
};

export async function commitPostDocument(params: {
  projectId: string;
  postId: string;
  data: Record<string, unknown>;
  timeoutMs?: number;
  dependencies: CommitPostDependencies;
  signal?: AbortSignal;
}) {
  const controller = new AbortController();
  const forwardAbort = () => controller.abort();
  params.signal?.addEventListener("abort", forwardAbort, { once: true });
  const timeout = setTimeout(() => controller.abort(), params.timeoutMs ?? 30_000);

  try {
    const token = await params.dependencies.getToken();
    const documentName = `projects/${params.projectId}/databases/(default)/documents/posts/${params.postId}`;
    const url = `${firestoreRestBaseUrl()}/v1/projects/${encodeURIComponent(params.projectId)}/databases/(default)/documents:commit`;
    const request = {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        writes: [
          {
            update: {
              name: documentName,
              fields: encodeFirestoreRestFields(params.data),
            },
            updateTransforms: [
              { fieldPath: "createdAt", setToServerValue: "REQUEST_TIME" },
            ],
            currentDocument: { exists: false },
          },
        ],
      }),
      signal: controller.signal,
    } satisfies RequestInit;

    let response: Response | undefined;
    let lastNetworkError: unknown;
    for (let attempt = 0; attempt < 2 && !response; attempt += 1) {
      try {
        // Browser fetch rejects an arbitrary `this` receiver. Invoke the injected
        // function directly, not as a method of the dependencies object.
        const fetchImpl = params.dependencies.fetchImpl;
        response = await fetchImpl(url, request);
      } catch (error) {
        lastNetworkError = error;
        if (controller.signal.aborted) break;
        if (attempt === 0) {
          await new Promise((resolve) => setTimeout(resolve, 1_000));
        }
      }
    }
    if (!response) throw lastNetworkError ?? new Error("Firestore network unavailable.");

    if (!response.ok) {
      let serverMessage = `Firestore HTTP ${response.status}`;
      try {
        const payload = (await response.json()) as { error?: { message?: string } };
        if (payload.error?.message) serverMessage = payload.error.message;
      } catch {
        // The HTTP status remains the authoritative diagnostic.
      }
      throw new PostFirestoreRestError(
        codeForHttpStatus(response.status),
        serverMessage,
        response.status
      );
    }
  } catch (error) {
    if (error instanceof PostFirestoreRestError) throw error;
    if (controller.signal.aborted) {
      throw new PostFirestoreRestError("deadline-exceeded", "Firestore REST timeout.");
    }
    throw new PostFirestoreRestError(
      "unavailable",
      error instanceof Error ? error.message : "Firestore REST error."
    );
  } finally {
    clearTimeout(timeout);
    params.signal?.removeEventListener("abort", forwardAbort);
  }
}

export async function readPostAuthorUid(params: {
  projectId: string;
  postId: string;
  timeoutMs?: number;
  dependencies: CommitPostDependencies;
  signal?: AbortSignal;
}) {
  const controller = new AbortController();
  const forwardAbort = () => controller.abort();
  params.signal?.addEventListener("abort", forwardAbort, { once: true });
  const timeout = setTimeout(() => controller.abort(), params.timeoutMs ?? 15_000);

  try {
    const token = await params.dependencies.getToken();
    const url =
      `${firestoreRestBaseUrl()}/v1/projects/${encodeURIComponent(params.projectId)}` +
      `/databases/(default)/documents/posts/${encodeURIComponent(params.postId)}` +
      "?mask.fieldPaths=authorUid";
    const fetchImpl = params.dependencies.fetchImpl;
    const response = await fetchImpl(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });

    if (response.status === 404) return null;
    if (!response.ok) {
      throw new PostFirestoreRestError(
        codeForHttpStatus(response.status),
        `Firestore HTTP ${response.status}`,
        response.status
      );
    }

    const payload = (await response.json()) as {
      fields?: { authorUid?: { stringValue?: unknown } };
    };
    const authorUid = payload.fields?.authorUid?.stringValue;
    return typeof authorUid === "string" ? authorUid : null;
  } catch (error) {
    if (error instanceof PostFirestoreRestError) throw error;
    if (controller.signal.aborted) {
      throw new PostFirestoreRestError(
        "deadline-exceeded",
        "Firestore REST timeout."
      );
    }
    throw new PostFirestoreRestError(
      "unavailable",
      error instanceof Error ? error.message : "Firestore REST error."
    );
  } finally {
    clearTimeout(timeout);
    params.signal?.removeEventListener("abort", forwardAbort);
  }
}

export async function createPostDocumentViaRest(
  postId: string,
  data: Record<string, unknown>,
  options?: { token?: string; signal?: AbortSignal; timeoutMs?: number; expectedUid?: string }
) {
  const user = auth.currentUser;
  const projectId = app.options.projectId;
  if (!user || !projectId || (options?.expectedUid && user.uid !== options.expectedUid)) {
    throw new PostFirestoreRestError("unauthenticated", "Session Firebase indisponible.");
  }

  return commitPostDocument({
    projectId,
    postId,
    data,
    timeoutMs: options?.timeoutMs,
    signal: options?.signal,
    dependencies: {
      getToken: () => {
        if (options?.expectedUid && auth.currentUser?.uid !== options.expectedUid) {
          throw new PostFirestoreRestError("unauthenticated", "La session a change pendant la publication.");
        }
        return options?.token ? Promise.resolve(options.token) : user.getIdToken();
      },
      fetchImpl: expoFetch as unknown as typeof fetch,
    },
  });
}

export async function readPostAuthorUidViaRest(
  postId: string,
  options?: {
    token?: string;
    signal?: AbortSignal;
    timeoutMs?: number;
    expectedUid?: string;
  }
) {
  const user = auth.currentUser;
  const projectId = app.options.projectId;
  if (
    !user ||
    !projectId ||
    (options?.expectedUid && user.uid !== options.expectedUid)
  ) {
    throw new PostFirestoreRestError(
      "unauthenticated",
      "Session Firebase indisponible."
    );
  }

  return readPostAuthorUid({
    projectId,
    postId,
    timeoutMs: options?.timeoutMs,
    signal: options?.signal,
    dependencies: {
      getToken: () =>
        options?.token ? Promise.resolve(options.token) : user.getIdToken(),
      fetchImpl: expoFetch as unknown as typeof fetch,
    },
  });
}
